import { z } from 'zod';
import { INTERVENTION_STATUSES, ROLES, SLOT_STATUSES, WAITING_LIST_STATUSES } from '../enums';
import { idSchema } from './common';
/**
 * Formes des reponses de l'API que le front consomme.
 *
 * Elles etaient auparavant redeclarees dans les composants — jusqu'a deux fois pour
 * `TechnicianSummary`. Un champ renomme cote serveur ne cassait alors rien a la
 * compilation : le front continuait de lire une propriete devenue absente, et
 * l'erreur n'apparaissait qu'a l'execution. Les declarer ici retablit le contrat.
 */
// --- Personnes --------------------------------------------------------------
/** Identite reduite, telle qu'elle accompagne une intervention ou un creneau. */
export const personSummarySchema = z.object({
    id: idSchema,
    firstName: z.string(),
    lastName: z.string(),
});
export const zoneSummarySchema = z.object({
    id: idSchema,
    name: z.string(),
    color: z.string(),
});
/** Technicien et les zones qu'il dessert — selecteurs d'affectation et planning. */
export const technicianSummarySchema = personSummarySchema.extend({
    email: z.string(),
    phone: z.string().nullable(),
    zones: z.array(zoneSummarySchema),
});
/** Ligne de la liste des utilisateurs du back-office. */
export const userSummarySchema = personSummarySchema.extend({
    email: z.string(),
    phone: z.string().nullable(),
    role: z.enum(ROLES),
    isGuest: z.boolean(),
    isActive: z.boolean(),
    createdAt: z.string(),
    lastLoginAt: z.string().nullable(),
    interventionCount: z.number().int(),
    bicycleCount: z.number().int(),
    city: z.string().nullable(),
});
// --- Planning ---------------------------------------------------------------
/** Creneau tel que le renvoie `GET /slots`, avec l'intervention qui l'occupe. */
export const slotWithInterventionSchema = z.object({
    id: idSchema,
    startsAt: z.string(),
    endsAt: z.string(),
    status: z.enum(SLOT_STATUSES),
    blockReason: z.string().nullable(),
    technicianId: idSchema,
    technician: personSummarySchema,
    zoneId: idSchema,
    zone: zoneSummarySchema,
    interventionId: idSchema.nullable(),
    intervention: z
        .object({
        id: idSchema,
        reference: z.string(),
        status: z.enum(INTERVENTION_STATUSES),
        packageName: z.string(),
    })
        .nullable(),
});
/** Modele de planification enrichi du nombre de creneaux deja generes. */
export const scheduleTemplateSummarySchema = z.object({
    id: idSchema,
    name: z.string(),
    technicianId: idSchema,
    technician: personSummarySchema,
    zoneId: idSchema,
    zone: zoneSummarySchema,
    weekdays: z.array(z.number().int()),
    startTime: z.string(),
    endTime: z.string(),
    slotDurationMin: z.number().int(),
    breakStartTime: z.string().nullable(),
    breakEndTime: z.string().nullable(),
    validFrom: z.string(),
    validTo: z.string(),
    isActive: z.boolean(),
    slotCount: z.number().int(),
});
// --- Liste d'attente --------------------------------------------------------
export const waitingListEntrySchema = z.object({
    id: idSchema,
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string().nullable(),
    street: z.string(),
    city: z.string(),
    postalCode: z.string(),
    message: z.string().nullable(),
    status: z.enum(WAITING_LIST_STATUSES),
    createdAt: z.string(),
});
// --- Tableau de bord --------------------------------------------------------
export const dashboardSchema = z.object({
    interventions: z.object({
        today: z.number().int(),
        upcoming: z.number().int(),
        pendingPayment: z.number().int(),
        byStatus: z.record(z.string(), z.number().int()),
    }),
    revenue30Days: z.object({ total: z.number(), count: z.number().int() }),
    capacity: z.object({
        technicians: z.number().int(),
        zones: z.number().int(),
        futureSlots: z.number().int(),
        availableSlots: z.number().int(),
        bookedSlots: z.number().int(),
        occupancyRate: z.number().int(),
    }),
    clients: z.object({ active: z.number().int(), waitingList: z.number().int() }),
});
// --- Journal d'audit --------------------------------------------------------
/** Entree du journal MongoDB. `changes` est volontairement libre : son contenu
 *  depend de l'entite auditee — c'est precisement ce qui a motive le choix d'une
 *  base documentaire. */
export const auditEntrySchema = z.object({
    timestamp: z.string(),
    action: z.string(),
    entity: z.string(),
    entityId: z.string().nullable(),
    actor: z.object({ id: idSchema, email: z.string(), role: z.string() }).nullable(),
    changes: z.object({ before: z.unknown().optional(), after: z.unknown().optional() }).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
//# sourceMappingURL=responses.js.map